import React, { useState, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import CloseSVG from '@birdi/icons/close.svg';
import WorkspaceSVG from '@birdi/icons/card-view.svg';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import { Tooltip, TooltipProvider } from '@birdi/components-common';
import type { WidgetId } from '../tabs';

const MAIN_ORG_ONLY_TOOLTIP =
  'Only available on the main organisation.';

export interface WidgetMeta {
  id: WidgetId;
  title: string;
  subtitle: string;
  description: string;
  category: 'cards' | 'graphs' | 'numbers' | 'other';
  imageUrl: string;
}

export const WIDGET_METADATA: WidgetMeta[] = [
  {
    id: 'map-coverage',
    title: 'Map coverage',
    subtitle: 'World map',
    description: 'A global view showing where your maps are located, giving a snapshot of coverage across all sites, projects and work areas.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/map-coverage.png',
  },
  {
    id: 'activity-trends',
    title: 'Activity trends',
    subtitle: 'Line graph',
    description: 'Visualizes overall activity over time: Such as uploads, processing, detections or reports. Helping you spot peaks, patterns and trends in usage.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/activity-trends.png',
  },
  {
    id: 'latest-maps',
    title: 'Latest maps',
    subtitle: 'Activity card',
    description: 'Shows recently created or updated maps, making it easy to jump back into ongoing work or pick up where the team left off.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/latest-maps.png',
  },
  {
    id: 'team-activity',
    title: 'Team activity',
    subtitle: 'Activity card',
    description: 'Provides a feed of the most recent team actions: Uploads, map processing, reports created and workspace update.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/team-activity.png',
  },
  {
    id: 'most-viewed-maps',
    title: 'Most viewed maps',
    subtitle: 'Activity card',
    description: 'Highlights your most active maps based on views, comments, and collaboration activity to quickly identify key \'source of truth\' projects.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/most-viewed-maps.png',
  },
  {
    id: 'recently-shared-maps',
    title: 'Recently shared maps',
    subtitle: 'Activity card',
    description: 'Shows maps recently shared with team members or external users, including collaborators and share activity to support ongoing coordination.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/recently-shared-maps.png',
  },
  {
    id: 'latest-map-reports',
    title: 'Latest map reports',
    subtitle: 'Activity card',
    description: 'Displays your latest generated reports, including volumes, inspections and map-based outputs, giving you fast access to what\'s been processed and shared.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/latest-map-reports.png',
  },
  {
    id: 'recent-comments',
    title: 'Latest comments',
    subtitle: 'Activity card',
    description: 'Highlights the newest comments, mentions and discussions happening across your team\'s maps, keeping you up to date with collaboration activity.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/latest-comments.png',
  },
  {
    id: 'latest-ai-detections',
    title: 'Latest AI detections',
    subtitle: 'Activity card',
    description: 'Shows the most recent AI detections across your imagery and maps, helping you quickly spot issues, changes, or objects identified by Birdi\'s AI.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/latest-ai-detections.png',
  },
  {
    id: 'processing',
    title: 'Processing',
    subtitle: 'Activity card',
    description: 'Displays current and recently completed processing tasks, including orthomosaics, DEMs, 3D models and point clouds.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/processing.png',
  },
  {
    id: 'processing-usage',
    title: 'Processing usage',
    subtitle: 'Activity card',
    description: 'Shows processing activity over time in Gigapixels (GP), helping you track usage patterns and plan your processing needs.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/processing-usage.png',
  },
  {
    id: 'processing-reports',
    title: 'Processing reports',
    subtitle: 'Activity card',
    description: 'Displays recently generated processing reports from your maps, with status updates and quick access to downloadable PDFs.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/processing-reports.png',
  },
  {
    id: 'processing-overview',
    title: 'Processing overview',
    subtitle: 'Activity card',
    description: 'Provides a high-level summary of processing accuracy, including resolution, coverage area, flight details, and reprojection metric.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/processing-overview.png',
  },
  {
    id: 'recent-workspaces',
    title: 'Recent workspaces',
    subtitle: 'Activity card',
    description: 'View recently active workspaces and keep track of project activity.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/recent-workspaces.png',
  },
  {
    id: 'recently-invited-users',
    title: 'Recently invited users',
    subtitle: 'Activity card',
    description: 'View the most recent users invited to your workspace and monitor team access.',
    category: 'cards',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/recently-invited-users.png',
  },
  {
    id: 'team-members',
    title: 'Team members',
    subtitle: 'Number',
    description: 'The total number of active team members in your organisation.',
    category: 'numbers',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/team-members.png',
  },
  {
    id: 'guest-collaborators',
    title: 'Guest collaborators',
    subtitle: 'Number',
    description: 'Shows the number of guest users currently collaborating across your maps and workspaces.',
    category: 'numbers',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/guest-collaborations.png',
  },
  {
    id: 'files-uploaded',
    title: 'Files uploaded',
    subtitle: 'Number',
    description: 'Shows the total count of uploaded files (imagery, vectors, documents and datasets).',
    category: 'numbers',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/files-uploaded.png',
  },
  {
    id: 'storage-used',
    title: 'Storage used',
    subtitle: 'Number',
    description: 'Indicates the total amount of storage currently being used by your organisation.',
    category: 'numbers',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/storage-used.png',
  },
  {
    id: 'active-workspaces',
    title: 'Active workspaces',
    subtitle: 'Number',
    description: 'Displays the total number of workspaces that are currently active.',
    category: 'numbers',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/active-workspaces.png',
  },
  {
    id: 'maps-created',
    title: 'Total maps created',
    subtitle: 'Number',
    description: 'The cumulative number of maps created in your organisation since onboarding.',
    category: 'numbers',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/maps-created.png',
  },
  {
    id: 'seats-usage',
    title: 'Seats usage',
    subtitle: 'Pie graph',
    description: 'Shows how many assigned seats are in use compared to your plan limit, helping you manage team access at a glance.',
    category: 'graphs',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/seats-usage.png',
  },
  {
    id: 'guest-access-usage',
    title: 'Guest access usage',
    subtitle: 'Pie graph',
    description: 'Displays how many of your available guest collaboration slots are currently being used.',
    category: 'graphs',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/guest-access-usage.png',
  },
  {
    id: 'workspace-usage',
    title: 'Workspace usage',
    subtitle: 'Pie graph',
    description: 'Shows the number of workspaces in use versus your total allowance, helping you monitor workspace allocation.',
    category: 'graphs',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/workspace-usage.png',
  },
  {
    id: 'processing-usage-graph',
    title: 'Processing usage',
    subtitle: 'Pie graph',
    description: 'Visualizes your used vs. remaining processing allowance (GPs), so you always know how much processing capacity you have left.',
    category: 'graphs',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/processing-usage-graph.png',
  },
  {
    id: 'storage-usage',
    title: 'Storage usage',
    subtitle: 'Pie graph',
    description: 'Shows how much storage your organisation has used compared to the total available, broken down into used and remaining.',
    category: 'graphs',
    imageUrl: 'https://birdi-public.s3-ap-southeast-2.amazonaws.com/dashboard-widgets/storage-usage.png',
  },
];

/** Pie graphs + usage-tab cards that only apply at main org level */
export const MAIN_ORG_ONLY_WIDGET_IDS: WidgetId[] = [
  'seats-usage',
  'guest-access-usage',
  'workspace-usage',
  'processing-usage-graph',
  'storage-usage',
  'recent-workspaces',
  'recently-invited-users',
];

export interface WidgetSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWidgetsChange: (widgetIds: WidgetId[]) => void;
  selectedWidgetIds?: WidgetId[];
  /** Sub-workspaces: grey out org-level widgets; Add disabled (Remove still works if already added). */
  restrictMainOrgWidgets?: boolean;
}

type CategoryFilter = 'all' | 'cards' | 'graphs' | 'numbers' | 'other';

const ContactUsCard: React.FC = () => {
  const theme = useTheme();

  return (
    <div
      css={css`
        min-height: 350px;
        display: flex;
        flex-direction: column;
        border: 1px solid ${theme.mono30};
        border-radius: 8px;
        overflow: hidden;
        background: ${theme.mono20};
      `}
    >
      {/* Content */}
      <div
        css={css`
          padding: 2em;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 0.5em;
          flex: 1;
          text-align: center;
          color: ${theme.midnight};
        `}
      >
        <h3
          css={css`
            margin: 0;
            font-size: 1.1em;
            font-weight: 600;
          `}
        >
          Don't see the report you need?
        </h3>
        <p
          css={css`
            margin: 1em 0;
            font-size: 0.9em;
          `}
        >
          Request a new report type and help shape our roadmap.
        </p>
      </div>

      {/* Contact Button */}
      <div
        css={css`
          padding: 1.5em;
        `}
      >
        <a
          href="mailto:support@birdi.io"
          css={css`
            display: block;
            width: calc(100% - 2em);
            padding: 0.5em 1em;
            border: 1px solid ${theme.deepOcean};
            border-radius: 6px;
            background: ${theme.deepOcean};
            color: ${theme.mono0};
            font-weight: 500;
            font-size: 0.9em;
            text-decoration: none;
            text-align: center;
            transition: background 0.2s;
            &:hover {
              background: ${theme.ocean};
            }
          `}
        >
          Contact us
        </a>
      </div>
    </div>
  );
};

export const WidgetSelectorModal: React.FC<WidgetSelectorModalProps> = ({
  open,
  onOpenChange,
  selectedWidgetIds = [],
  onWidgetsChange,
  restrictMainOrgWidgets = false,
}) => {
  const theme = useTheme();
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [selectedWidgets, setSelectedWidgets] = useState<Set<WidgetId>>(
    new Set(selectedWidgetIds as WidgetId[])
  );

  // Reset to "all" tab when modal opens (not when widgets change)
  React.useEffect(() => {
    if (open) {
      setCategoryFilter('all');
    }
  }, [open]);

  // Update selected widgets when prop changes or modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedWidgets(new Set(selectedWidgetIds as WidgetId[]));
    }
  }, [selectedWidgetIds, open]);

  const filteredWidgets = useMemo(() => {
    if (categoryFilter === 'all') {
      return WIDGET_METADATA;
    }
    return WIDGET_METADATA.filter((widget) => widget.category === categoryFilter);
  }, [categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts = {
      all: WIDGET_METADATA.length + 1, // +1 for ContactUsCard
      cards: 0,
      graphs: 0,
      numbers: 0,
      other: 1, // ContactUsCard
    };
    WIDGET_METADATA.forEach((widget) => {
      counts[widget.category]++;
    });
    return counts;
  }, []);

  const handleToggleWidget = (widgetId: WidgetId) => {
    const newSelected = new Set(selectedWidgets);
    if (newSelected.has(widgetId)) {
      newSelected.delete(widgetId);
    } else {
      if (
        restrictMainOrgWidgets &&
        MAIN_ORG_ONLY_WIDGET_IDS.includes(widgetId)
      ) {
        return;
      }
      newSelected.add(widgetId);
    }
    setSelectedWidgets(newSelected);
    onWidgetsChange(Array.from(newSelected));
  };

  const categories: { id: CategoryFilter; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: categoryCounts.all },
    { id: 'cards', label: 'Activity cards', count: categoryCounts.cards },
    { id: 'graphs', label: 'Graphs', count: categoryCounts.graphs },
    { id: 'numbers', label: 'Numbers', count: categoryCounts.numbers },
    { id: 'other', label: 'Other', count: categoryCounts.other },
  ];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <div css={portalCSS}>
          <Dialog.Content
            css={(theme) => css`
              ${contentCSS(theme)};
              width: 60em;
              max-width: 90vw;
              max-height: 90vh;
              overflow-y: auto;
            `}
          >
            <Dialog.Close css={closeButtonCSS}>
              <CloseSVG />
            </Dialog.Close>
            <TooltipProvider delayDuration={200}>
            <h2
              css={css`
                margin: 0 0 1em 0;
                font-size: 1.5em;
                font-weight: 600;
                color: ${theme.midnight};
                display: flex;
                align-items: center;
                gap: 0.5em;
              `}
            >
              <WorkspaceSVG />
              Select a widget to add it to your dashboard
            </h2>

            {/* Line after header */}
            <div
              css={css`
                height: 2px;
                background: ${theme.mono30};
                margin-bottom: 1.25em;
              `}
            />

            {/* Category Tabs */}
            <div
              css={css`
                display: flex;
                gap: 0.5em;
                margin-bottom: 1.25em;
                width: 100%;
              `}
            >
              {categories.map((category) => {
                const isActive = categoryFilter === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => setCategoryFilter(category.id)}
                    css={css`
                      flex: 1;
                      background: ${isActive ? theme.lightOcean : theme.mono10};
                      border: 1px solid ${isActive ? theme.deepOcean : theme.mono30};
                      padding: 0.75em 1em;
                      font-size: 0.95em;
                      font-weight: ${isActive ? '600' : '400'};
                      color: ${theme.midnight};
                      cursor: pointer;
                      border-radius: 12px;
                      white-space: nowrap;
                      transition: all 0.2s;
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      gap: 0.75em;
                      &:hover {
                        border-color: ${isActive ? theme.deepOcean : theme.mono40};
                        box-shadow: 0 1px 1px 2px ${theme.mono20};
                      }
                    `}
                  >
                    <span>{category.label}</span>
                    <span
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 1.5em;
                        height: 1.5em;
                        padding: 0 0.5em;
                        border-radius: 12px;
                        font-size: 0.85em;
                        font-weight: 500;
                        background: ${isActive ? theme.deepOcean : theme.mono20};
                        color: ${isActive ? theme.mono0 : theme.mono70};
                        transition: background 0.2s, color 0.2s;
                      `}
                    >
                      {category.count}
                    </span>
                </button>
                );
              })}
            </div>

            {/* Widget Grid */}
            <div
              css={css`
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 1em;
                @media (max-width: ${breakpoints.m}) {
                grid-template-columns: repeat(2, 1fr);
                }
                @media (max-width: ${breakpoints.xs}) {
                  grid-template-columns: 1fr;
                }
              `}
            >
              {filteredWidgets.map((widget) => {
                const isSelected = selectedWidgets.has(widget.id);
                const locked =
                  restrictMainOrgWidgets &&
                  MAIN_ORG_ONLY_WIDGET_IDS.includes(widget.id) &&
                  !isSelected;
                return (
                  <div
                    key={widget.id}
                    css={css`
                      display: flex;
                      flex-direction: column;
                      border: 1px solid ${theme.mono30};
                      border-radius: 8px;
                      overflow: hidden;
                      background: ${theme.mono0};
                      transition: box-shadow 0.2s;
                      position: relative;
                      isolation: isolate;
                      &:hover {
                        box-shadow: ${locked
                          ? 'none'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)'};
                      }
                      ${locked &&
                      css`
                        &::before {
                          content: '';
                          position: absolute;
                          inset: 0;
                          z-index: 1;
                          border-radius: 7px;
                          background: ${theme.mono20};
                          opacity: 0.5;
                          pointer-events: none;
                        }
                      `}
                    `}
                  >
                    {/* Widget Info */}
                    <div
                      css={css`
                        padding: 1.25em 1.25em 0 1.25em;
                        display: flex;
                        flex-direction: column;
                        gap: 0.5em;
                        flex: 1;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          flex-direction: row;
                          align-items: flex-start;
                        `}
                        >
                        <h3
                          css={css`
                            margin: 0;
                            font-size: 1em;
                            font-weight: 500;
                            color: ${theme.midnight};
                          `}
                        >
                          {widget.title}
                        </h3>
                        <span
                          css={css`
                            font-size: 0.9em;
                            color: ${theme.mono50};
                          `}
                        >
                          &nbsp;|&nbsp;
                          {widget.subtitle}
                        </span>
                      </div>
                      <p
                        css={css`
                          margin: 0;
                          font-size: 0.9em;
                          color: ${theme.mono60};
                          line-height: 1.5;
                        `}
                      >
                        {widget.description}
                      </p>
                    </div>

                    {/* Widget Preview Image */}
                    <div
                      css={css`
                        width: calc(100% - 2.5em);
                        height: 175px;
                        background: ${theme.mono20};
                        // border: 1px solid ${theme.mono20};
                        margin: 1em 1.25em;
                        border-radius: 8px;
                        overflow: hidden;
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      `}
                    >
                      <img
                        src={widget.imageUrl}
                        alt={widget.title}
                        css={css`
                          width: 100%;
                          height: 100%;
                          object-fit: contain;
                          object-position: center;
                        `}
                      />
                    </div>

                    {/* Add/Remove Button */}
                    <div
                      css={css`
                        padding: 0.5em 1.5em 1.5em 1.5em;
                      `}
                    >
                      {locked ? (
                        <Tooltip
                          delay={150}
                          contentProps={{ side: 'top' }}
                          trigger={
                            <span
                              css={css`
                                display: block;
                                width: 100%;
                              `}
                            >
                              <button
                                type="button"
                                disabled
                                css={css`
                                  width: 100%;
                                  padding: 0.5em 1em;
                                  border: 1px solid ${theme.mono40};
                                  border-radius: 6px;
                                  background: ${theme.mono30};
                                  color: ${theme.mono60};
                                  font-weight: 500;
                                  cursor: not-allowed;
                                `}
                              >
                                + Add
                              </button>
                            </span>
                          }
                        >
                          {MAIN_ORG_ONLY_TOOLTIP}
                        </Tooltip>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleToggleWidget(widget.id)}
                          css={css`
                            width: 100%;
                            padding: 0.5em 1em;
                            border: 1px solid ${theme.deepOcean};
                            border-radius: 6px;
                            background: ${isSelected
                              ? theme.mono0
                              : theme.deepOcean};
                            color: ${isSelected
                              ? theme.deepOcean
                              : theme.mono0};
                            font-weight: 500;
                            cursor: pointer;
                            transition: background 0.2s, color 0.2s;
                            &:hover {
                              background: ${isSelected ? theme.lightOcean : theme.deepOcean};
                              color: ${isSelected ? theme.deepOcean : theme.mono0};
                            }
                          `}
                        >
                          {isSelected ? '- Remove' : '+ Add'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Contact Us Card - shown in "all" and "other" categories */}
              {(categoryFilter === 'all' || categoryFilter === 'other') && (
                <ContactUsCard />
              )}
            </div>
            </TooltipProvider>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

